import { getToken } from 'firebase/app-check';
import type { User } from 'firebase/auth';
import { isAppCheckDeadlineError, waitForAppCheckAttempt } from './appCheckDeadline';
import { appCheck } from './firebase';

/**
 * How much longer a Function call waits for a slow reCAPTCHA once App Check's
 * own two second limit has passed (lib/appCheckDeadline.ts). About forty six
 * Functions refuse a call without a real token (appCheckGuard with
 * `required: true`), so going ahead early would turn a slow but working check
 * into a 401. Before that limit existed this wait had no end at all; a
 * reCAPTCHA that never loads now costs this long and then a clear refusal
 * from the server, instead of a spinner forever. It is counted from when the
 * reCAPTCHA attempt STARTED, so on a page where it never loads only the first
 * call pays for the wait and every later one gets its answer at once.
 */
export const FUNCTION_APP_CHECK_EXTRA_WAIT_MS = 30_000;

/** App Check headers for public, browser-invoked Netlify Functions. */
export async function publicFunctionHeaders(
  options: { json?: boolean } = {}
): Promise<Record<string, string>> {
  const headers: Record<string, string> = {};
  if (options.json !== false) headers['Content-Type'] = 'application/json';

  if (appCheck) {
    try {
      const token = await getToken(appCheck, false);
      if (token.token) headers['X-Firebase-AppCheck'] = token.token;
    } catch (error) {
      // The server decides whether App Check is mandatory. Keeping this
      // best-effort lets local development show a useful server error.
      if (isAppCheckDeadlineError(error)) {
        await waitForAppCheckAttempt(FUNCTION_APP_CHECK_EXTRA_WAIT_MS);
        try {
          const token = await getToken(appCheck, false);
          if (token.token) headers['X-Firebase-AppCheck'] = token.token;
        } catch {
          // Still nothing: send the call anyway and let the server answer.
        }
      }
    }
  }

  return headers;
}

/** Authenticated headers for browser-invoked Netlify Functions. */
export async function functionHeaders(
  user: User,
  options: { json?: boolean; forceRefresh?: boolean } = {}
): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    ...(await publicFunctionHeaders(options)),
    Authorization: 'Bearer ' + (await user.getIdToken(options.forceRefresh === true)),
  };

  return headers;
}
